import Decimal from "decimal.js";

import { HttpAction } from "@megaron/http-service";
import { Serializers } from "@megaron/serializers";
import { Uuid, uuidSerializer } from "@megaron/uuid";

import { availableRewardDtoSerializer } from "./availableRewardDto";
import { OriginBreakdown, originBreakdownSerializer, PointTransfer, pointTransferSerializer } from "./pointCategory";

export type Purchase = {
  uuid: Uuid;
  userUuid: Uuid;
  listingUuid: Uuid;
  rewardId: string;
  price: Decimal;
  time: Date;
  isBuyerPro: boolean;
  isRefunded: boolean;
  transfers: PointTransfer[];
  originBreakdown: OriginBreakdown;
};

export const purchaseSerializer = Serializers.object<Purchase>({
  uuid: uuidSerializer,
  userUuid: uuidSerializer,
  listingUuid: uuidSerializer,
  rewardId: Serializers.string,
  price: Serializers.decimal,
  time: Serializers.datetime,
  transfers: pointTransferSerializer.array(),
  isBuyerPro: Serializers.boolean,
  isRefunded: Serializers.boolean,
  originBreakdown: originBreakdownSerializer,
});

export type PurchaseSimple = {
  uuid: Uuid;
  userUuid: Uuid;
  listingUuid: Uuid;
  rewardId: string;
  price: Decimal;
  time: Date;
};

export const purchaseSimpleSerializer = Serializers.object<PurchaseSimple>({
  uuid: uuidSerializer,
  userUuid: uuidSerializer,
  listingUuid: uuidSerializer,
  rewardId: Serializers.string,
  price: Serializers.decimal,
  time: Serializers.datetime,
});

const myAvailableRewards = HttpAction({
  path: "/myAvailableRewards",
  method: "get",
  requiresAuth: true as const,
  valueSerializer: Serializers.array(availableRewardDtoSerializer),
});

const availableRewards = HttpAction({
  path: "/availableRewards",
  method: "get",
  valueSerializer: Serializers.array(availableRewardDtoSerializer),
});

const buyReward = HttpAction({
  path: "/buyReward",
  method: "post",
  bodySerializer: Serializers.object({
    listingUuid: uuidSerializer,
    rewardId: Serializers.string,
    ...Serializers.object({
      accountId: uuidSerializer,
    }).partial().fields,
  }),
  valueSerializer: Serializers.object({
    purchaseUuid: uuidSerializer,
  }),
  errorSerializer: Serializers.stringOneOf(
    "NotEnoughPoints",
    "ListingNotFound",
    "ListingUnavailable",
    "ProStatusRequired",
    "RewardNotListed",
    "AccountNotFound",
  ),
  requiresAuth: true as const,
});

const purchases = HttpAction({
  path: "/purchases",
  method: "get",
  requiresAuth: true as const,
  qsSerializer: Serializers.object({
    since: Serializers.datetime.optional(),
  }),
  valueSerializer: Serializers.array(purchaseSimpleSerializer),
});

const purchaseQuery = HttpAction({
  path: "/purchases/uuid/:purchaseUuid",
  method: "get",
  paramsSerializer: Serializers.object({
    purchaseUuid: uuidSerializer,
  }),
  requiresAuth: true as const,
  valueSerializer: purchaseSerializer.nullable(),
});

export const rewardActions = {
  myAvailableRewards,
  availableRewards,
  buyReward,
  purchases,
  purchaseQuery,
};
